// TripCard Refactor — Data Models and Type Definitions
//
// JSDoc-typed interfaces for the tree-view hierarchical TripCard layout.
// These types define the data contracts between the stage classification,
// next action engine, navigation guidance, and UI components.
//
// Validates: Requirements 2.1, 2.3, 4.4, 4.5, 7.5, 7.7

/**
 * @typedef {'departure_from_origin' | 'airport_entry' | 'security_checkin' | 'navigate_to_gate' | 'waiting_boarding' | 'in_flight' | 'arrival_navigation' | 'ground_transport'} JourneyStage
 */

/**
 * @typedef {'completed' | 'active' | 'upcoming'} StageState
 */

/**
 * @typedef {'critical' | 'warning'} AlertType
 */

/**
 * @typedef {'cancellation' | 'delay' | 'gate_change'} AlertSource
 */

/**
 * Alert information attached to a stage or event.
 *
 * @typedef {Object} AlertInfo
 * @property {AlertType} type - Severity: 'critical' for cancellations, 'warning' for delays/gate changes
 * @property {AlertSource} source - What triggered the alert
 * @property {string} message - Human-readable alert description
 * @property {string} affectedEventId - ID of the event that triggered this alert
 */

/**
 * Badge displayed on a collapsed StageNode showing summary status.
 *
 * @typedef {Object} StageBadge
 * @property {number} eventCount - Number of events in this stage
 * @property {string} relativeTime - Formatted as "in Xh Ym" (>1h), "in X min" (<1h), or "now" (active)
 * @property {boolean} hasAlert - Whether any event in the stage has an active alert
 * @property {AlertType} [alertType] - Most severe alert type if hasAlert is true
 */

/**
 * Data model for a single stage node in the tree-view hierarchy.
 *
 * @typedef {Object} StageNodeData
 * @property {string} id - Unique identifier, e.g. "stage:leg0:in_flight"
 * @property {JourneyStage} stage - Which journey stage this node represents
 * @property {StageState} stageState - Visual state classification
 * @property {Array<Object>} events - ClassifiedEvents assigned to this stage
 * @property {StageBadge} badge - Summary badge for collapsed display
 * @property {AlertInfo} [alert] - Alert info if this stage has an active alert condition
 * @property {AirportIntelligenceData} [navigationGuidance] - Navigation data for arrival/gate stages
 * @property {boolean} isVisible - False when stage has zero events (should not render)
 */

/**
 * Next action computed by the nextActionEngine for the banner.
 *
 * @typedef {Object} NextAction
 * @property {string} instruction - Action description, max 80 characters
 * @property {Date} deadline - When this action must be completed by
 * @property {NextActionButton} [actionButton] - Contextual button when a digital action is available
 * @property {boolean} isIdle - True when no pending action exists
 * @property {string} [idleMessage] - Shown when isIdle, e.g. "Next step in 2 hours" or "Trip complete"
 */

/**
 * Contextual action button for the NextActionBanner.
 *
 * @typedef {Object} NextActionButton
 * @property {string} label - Button text, e.g. "Get Directions", "Call Uber"
 * @property {'navigation' | 'booking' | 'document' | 'rideshare'} actionType - Category of action
 * @property {Object} payload - Action-specific data for the handler
 */

/**
 * Compact card data for the trips list view.
 *
 * @typedef {Object} CompactCardData
 * @property {string} tripName - Display name of the trip
 * @property {{ start: Date, end: Date }} dateRange - Trip start and end dates
 * @property {string} currentStageLabel - Human-readable label for the current stage
 * @property {string} nextActionSummary - Truncated to 80 characters max
 * @property {{ completed: number, total: number }} progress - Completed stages out of total stages with events
 * @property {Array<AlertBadge>} alerts - Active alerts for badge display
 */

/**
 * Alert badge for compact card view.
 *
 * @typedef {Object} AlertBadge
 * @property {AlertType} type - Alert severity
 * @property {AlertSource} source - What triggered the alert
 * @property {string} message - Short alert description
 */

/**
 * Airport intelligence data for navigation guidance.
 *
 * @typedef {Object} AirportIntelligenceData
 * @property {string} [gateRelativeDirections] - e.g. "Turn LEFT out of Gate B12"
 * @property {number} [walkingTimeMinutes] - Estimated walking time in minutes
 * @property {Array<LoungeInfo>} [lounges] - Available lounges near the traveler
 * @property {string} [baggageCarousel] - Carousel number/identifier
 * @property {string} [terminalExitDirections] - Directions to terminal exit
 */

/**
 * Lounge information for gate navigation suggestions.
 *
 * @typedef {Object} LoungeInfo
 * @property {string} name - Lounge name, e.g. "Admirals Club"
 * @property {string} terminal - Terminal where the lounge is located
 * @property {number} [walkingTimeMinutes] - Walking time from current position
 * @property {string} [directions] - Walking directions to the lounge
 * @property {Array<string>} memberships - Required memberships for access
 */

/**
 * Ground transport booking data for arrival navigation.
 *
 * @typedef {Object} GroundTransportBooking
 * @property {'uber' | 'lyft' | 'rental_car' | 'shuttle' | 'transit'} type - Transport type
 * @property {string} [pickupLocation] - Where to meet the transport
 * @property {string} [pickupTime] - Scheduled pickup time (ISO string)
 * @property {string} [confirmationNumber] - Booking confirmation reference
 * @property {string} [walkingDirections] - How to get to the pickup point
 */

/**
 * Navigation guidance data computed for arrival and gate navigation stages.
 *
 * @typedef {Object} NavigationGuidanceData
 * @property {'arrival_navigation' | 'navigate_to_gate'} stage - Which navigation stage
 * @property {string} primaryDirection - Main directional instruction
 * @property {Array<NavigationStep>} steps - Sequential step list
 * @property {number} activeStepIndex - Index of the currently active step
 * @property {string} [walkingTime] - Formatted walking time, e.g. "8 min walk"
 * @property {LoungeInfo} [loungeSuggestion] - Suggested lounge if eligible
 * @property {string} [baggageInfo] - Baggage carousel info
 * @property {GroundTransportBooking} [groundTransport] - Pre-booked transport details
 * @property {GateChangeAlert} [gateChangeAlert] - Active gate change alert
 */

/**
 * A single step in the navigation sequence.
 *
 * @typedef {Object} NavigationStep
 * @property {string} id - Unique step identifier
 * @property {string} label - Step description, e.g. "Navigate to Baggage Claim"
 * @property {boolean} isActive - Whether this is the current step
 * @property {boolean} isCompleted - Whether this step is done
 */

/**
 * Gate change alert data for navigation guidance.
 *
 * @typedef {Object} GateChangeAlert
 * @property {string} oldGate - Previous gate assignment
 * @property {string} newGate - Updated gate assignment
 * @property {number} [updatedWalkingTime] - New walking time in minutes
 * @property {string} [updatedDirections] - New directions to the gate
 */

// ── Exported constants ──────────────────────────────────────

/**
 * Canonical order of the eight journey stages used for rendering.
 * Stages are always displayed in this order regardless of event timing.
 */
export const JOURNEY_STAGE_ORDER = [
  'departure_from_origin',
  'airport_entry',
  'security_checkin',
  'navigate_to_gate',
  'waiting_boarding',
  'in_flight',
  'arrival_navigation',
  'ground_transport',
];

/**
 * Maps alert sources to their severity type.
 */
export const ALERT_TYPE_MAP = {
  cancellation: 'critical',
  delay: 'warning',
  gate_change: 'warning',
};

/**
 * Maximum character length for next action instructions.
 */
export const MAX_INSTRUCTION_LENGTH = 80;

/**
 * Creates a default empty StageBadge.
 *
 * @param {number} eventCount - Number of events in the stage
 * @returns {StageBadge}
 */
export function createStageBadge(eventCount = 0) {
  return {
    eventCount,
    relativeTime: '',
    hasAlert: false,
    alertType: undefined,
  };
}

/**
 * Creates a default empty StageNodeData.
 *
 * @param {string} id - Node identifier
 * @param {JourneyStage} stage - Journey stage
 * @param {StageState} stageState - Visual state
 * @returns {StageNodeData}
 */
export function createStageNodeData(id, stage, stageState = 'upcoming') {
  return {
    id,
    stage,
    stageState,
    events: [],
    badge: createStageBadge(0),
    alert: undefined,
    navigationGuidance: undefined,
    isVisible: false,
  };
}

/**
 * Creates a default idle NextAction.
 *
 * @param {string} [idleMessage] - Message to display when idle
 * @returns {NextAction}
 */
export function createIdleNextAction(idleMessage = 'Trip complete') {
  return {
    instruction: '',
    deadline: new Date(),
    actionButton: undefined,
    isIdle: true,
    idleMessage,
  };
}

/**
 * Creates a GroundTransportBooking object.
 *
 * @param {Object} params
 * @param {'uber' | 'lyft' | 'rental_car' | 'shuttle' | 'transit'} params.type
 * @param {string} [params.pickupLocation]
 * @param {string} [params.pickupTime]
 * @param {string} [params.confirmationNumber]
 * @param {string} [params.walkingDirections]
 * @returns {GroundTransportBooking}
 */
export function createGroundTransportBooking({
  type,
  pickupLocation,
  pickupTime,
  confirmationNumber,
  walkingDirections,
} = {}) {
  return {
    type: type || 'transit',
    pickupLocation: pickupLocation || undefined,
    pickupTime: pickupTime || undefined,
    confirmationNumber: confirmationNumber || undefined,
    walkingDirections: walkingDirections || undefined,
  };
}
